import React, { useEffect, useState } from 'react';
import { createEquipment, updateEquipment, deleteEquipment } from '../services/equipmentService';

const EMPTY_FORM = { name: '', type: '', brand: '' };

export default function EquipmentCatalogModal({ items, onClose, onChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  function selectItem(item) {
    setPendingDelete(null);
    setError('');
    if (editingItem?.id === item.id) {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    } else {
      setEditingItem(item);
      setForm({ name: item.name || '', type: item.type || '', brand: item.brand || '' });
    }
  }

  function cancelEdit() {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setPendingDelete(null);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      type: form.type.trim(),
      brand: form.brand.trim(),
    };

    try {
      if (editingItem) {
        await updateEquipment(editingItem.id, { ...payload, state: editingItem.state });
      } else {
        await createEquipment(payload);
      }
      cancelEdit();
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo guardar el equipo.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleState(item) {
    setError('');
    setBusy(true);
    try {
      await updateEquipment(item.id, { name: item.name, type: item.type, brand: item.brand, state: !item.state });
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo cambiar el estado.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setError('');
    setBusy(true);
    try {
      await deleteEquipment(pendingDelete.id);
      cancelEdit();
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo eliminar el equipo.');
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 320,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 580, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>Administrar Equipos</h6>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row g-3" style={{ marginBottom: 14 }}>
              <div className="col-12">
                <label className="form-label">Nombre</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Televisor Samsung 43&quot;"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="col-6">
                <label className="form-label">Tipo</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Televisor"
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                />
              </div>
              <div className="col-6">
                <label className="form-label">Marca</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Samsung"
                  value={form.brand}
                  onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                />
              </div>
            </div>

            {!pendingDelete ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="submit" className="btn-orig btn-orig-green" disabled={saving || busy}>
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
                {editingItem && (
                  <>
                    <button
                      type="button"
                      className="btn-orig btn-orig-outline"
                      onClick={() => toggleState(editingItem)}
                      disabled={saving || busy}
                    >
                      Cambiar estado
                    </button>
                    <button
                      type="button"
                      className="btn-orig btn-orig-outline"
                      onClick={() => setPendingDelete(editingItem)}
                      disabled={saving || busy}
                      style={{ color: 'var(--orig-red-light)' }}
                    >
                      Eliminar
                    </button>
                    <button
                      type="button"
                      className="btn-orig btn-orig-outline"
                      onClick={cancelEdit}
                      disabled={saving || busy}
                    >
                      Cancelar
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                background: 'var(--orig-red-dim)',
                border: '1px solid rgba(220,38,38,0.35)',
                borderRadius: 'var(--orig-radius)',
                padding: '10px 14px',
              }}>
                <span style={{ fontSize: 13, color: 'var(--orig-text)' }}>
                  ¿Eliminar <strong>{pendingDelete.name}</strong>?
                </span>
                <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                  <button type="button" className="btn-orig btn-orig-green" onClick={confirmDelete} disabled={busy}>
                    Sí, eliminar
                  </button>
                  <button type="button" className="btn-orig btn-orig-outline" onClick={() => setPendingDelete(null)} disabled={busy}>
                    No
                  </button>
                </div>
              </div>
            )}
          </form>

          <div style={{ marginTop: 20 }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text)' }}>
                Equipos existentes
              </span>{' '}
              <small style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                — haz clic para seleccionar
              </small>
            </div>

            {items.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: 'var(--orig-text-muted)', fontSize: 13 }}>
                No hay registros
              </div>
            ) : (
              <div style={{ border: '1px solid var(--orig-border)', borderRadius: 'var(--orig-radius)', overflow: 'hidden' }}>
                {items.map((item, idx) => (
                  <div
                    key={item.id}
                    onClick={() => selectItem(item)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: idx < items.length - 1 ? '1px solid var(--orig-border)' : 'none',
                      background: editingItem?.id === item.id ? 'var(--orig-green-dim)' : 'transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--orig-text)' }}>{item.name}</div>
                      {(item.type || item.brand) && (
                        <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                          {[item.type, item.brand].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className={`badge-orig ${item.state ? 'badge-green' : 'badge-red'}`}>
                        {item.state ? 'Activo' : 'Inactivo'}
                      </span>
                      <i className="bi bi-chevron-right" style={{ color: 'var(--orig-text-muted)' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
